import { useMemo } from "react";

import { resolveBookingView, type BookingView } from "@/lib/booking-view";
import { useNow } from "@/hooks/common/use-now";
import type { Booking } from "@/types/doctor";

export interface ClassifiedBooking {
  booking: Booking;
  view: BookingView;
}

/**
 * Files every booking into its display bucket against a ticking clock.
 *
 * The clock is what closes the gap around the backend's 5-minute missed sweep:
 * when a slot's end time passes, `now` advances, the booking re-resolves from
 * Booked to Missed, and both the dashboard and the bookings list move it — no
 * reload, no refetch, and no waiting for the job to land.
 */
export function useClassifiedBookings(bookings: Booking[] | undefined): {
  items: ClassifiedBooking[];
  now: Date;
} {
  const now = useNow();

  const items = useMemo(
    () =>
      (bookings ?? []).map((booking) => ({
        booking,
        view: resolveBookingView(booking, now),
      })),
    [bookings, now],
  );

  return { items, now };
}
